import { relations, sql } from "drizzle-orm";
import { bigint, boolean, date, integer, json, jsonb, numeric, pgEnum, pgTable, pgView, primaryKey, text, timestamp, unique, uuid, varchar, } from "drizzle-orm/pg-core";
import { conversationTable } from "./conversation.js";
import { dataRequestTable, dataRequestUpdateTable } from "./dataRequest.js";
import { dataRequestValidationTable } from "./dataRequestValidation.js";
import { dossier, dossierDocument, dossierUser } from "./dossier.js";
import { hdnLoanApplication, hdnMessage, hdnNode, hdnPartijNawData, hdnProvider, hdnProviderProduct, hdnServiceProvider, } from "./hdn.js";
import { notification } from "./notifications.js";
import { workItem } from "./workItems.js";
export const account = pgTable("account", {
    id: uuid("id")
        .default(sql `uuid_generate_v4()`)
        .primaryKey()
        .notNull(),
    emailAddress: varchar("email_address", { length: 255 }).unique(),
    firebaseId: varchar("firebase_id", { length: 128 }).notNull().unique(),
    verifiedAt: timestamp("verified_at", {
        withTimezone: true,
        mode: "string",
    }),
    creationDate: timestamp("creation_date", {
        withTimezone: true,
        mode: "string",
    })
        .defaultNow()
        .notNull(),
});
export const accountRelations = relations(account, ({ many }) => ({
    appUsers: many(appUser),
}));
export const agency = pgTable("agency", {
    name: varchar("name", { length: 255 }).notNull(),
    mainColor: json("main_color").$type(),
    afmNumber: text("afm_number"),
    emailAddress: text("email_address"),
    website: text("website"),
    phoneNumber: text("phone_number"),
    hdnNodeKvkNumber: text("hdn_node_kvk_number"),
    hdnNodeZipCode: text("hdn_node_zip_code"),
    hdnNodeNumberForVerification: text("hdn_node_number_for_verification"),
    hdnNodeNumber: text("hdn_node_number").references(() => hdnNode.nodeNumber, {
        onDelete: "set null",
    }),
    datakeeperTenantId: text("datakeeper_tenant_id"),
    datakeeperTenantActivatedAt: timestamp("datakeeper_tenant_activated_at", {
        withTimezone: true,
        mode: "string",
    }),
    financingDefaults: jsonb("financing_defaults").$type(),
    id: uuid("id")
        .default(sql `uuid_generate_v4()`)
        .primaryKey()
        .notNull(),
    creationDate: timestamp("creation_date", {
        withTimezone: true,
        mode: "string",
    })
        .defaultNow()
        .notNull(),
    dossierCount: integer("dossier_count").default(0).notNull(),
    conversationCount: integer("conversation_count").default(0).notNull(),
});
export const agencyRelations = relations(agency, ({ one, many }) => ({
    appUsers: many(appUser),
    dossiers: many(dossier),
    documents: many(dossierDocument),
    hdnMessages: many(hdnMessage),
    hdnLoanApplications: many(hdnLoanApplication),
    hdnPartijNawData: many(hdnPartijNawData),
    hdnNode: one(hdnNode, {
        fields: [agency.hdnNodeNumber],
        references: [hdnNode.nodeNumber],
    }),
    dataRequest: many(dataRequestTable),
    dataRequestValidations: many(dataRequestValidationTable),
    dataRequestUpdates: many(dataRequestUpdateTable),
    conversations: many(conversationTable),
    workItems: many(workItem),
}));
export const lender = pgTable("lender", {
    id: uuid("id")
        .default(sql `uuid_generate_v4()`)
        .primaryKey()
        .notNull(),
    hdnProviderCode: text("hdn_provider_code").references(() => hdnProvider.name, {
        onDelete: "set null",
        onUpdate: "cascade",
    }),
    name: text("name").notNull(),
    logo: text("logo"),
    icon: text("icon"),
    aliases: jsonb("aliases").$type(),
    interestDomains: jsonb("interest_domains").$type(),
    isActive: boolean("is_active").default(true).notNull(),
    createdAt: timestamp("created_at", {
        withTimezone: true,
        mode: "string",
    })
        .defaultNow()
        .notNull(),
});
export const lenderProduct = pgTable("lender_product", {
    id: uuid("id")
        .default(sql `uuid_generate_v4()`)
        .primaryKey()
        .notNull(),
    lenderId: uuid("lender_id")
        .notNull()
        .references(() => lender.id, {
        onDelete: "cascade",
        onUpdate: "cascade",
    }),
    hdnProductCode: text("hdn_product_code").references(() => hdnProviderProduct.productCode, {
        onDelete: "set null",
        onUpdate: "cascade",
    }),
    name: text("name").notNull(),
    isActive: boolean("is_active").default(true).notNull(),
    createdAt: timestamp("created_at", {
        withTimezone: true,
        mode: "string",
    })
        .defaultNow()
        .notNull(),
}, (table) => [
    unique("lender_product_lender_id_name_key").on(table.lenderId, table.name),
]);
export const lenderRelations = relations(lender, ({ one, many }) => ({
    hdnProvider: one(hdnProvider, {
        fields: [lender.hdnProviderCode],
        references: [hdnProvider.name],
    }),
    products: many(lenderProduct),
}));
export const lenderProductRelations = relations(lenderProduct, ({ one }) => ({
    lender: one(lender, {
        fields: [lenderProduct.lenderId],
        references: [lender.id],
    }),
    hdnProduct: one(hdnProviderProduct, {
        fields: [lenderProduct.hdnProductCode],
        references: [hdnProviderProduct.productCode],
    }),
}));
export const role = pgEnum("role", [
    "api",
    "customer",
    "intermediary",
    "admin",
    "transcription",
]);
export const appUser = pgTable("app_user", {
    fullName: varchar("full_name", { length: 80 }),
    emailAddress: varchar("email_address", { length: 255 }),
    accountId: uuid("account_id").references(() => account.id, {
        onDelete: "set null",
    }),
    role: role("role").default("customer").notNull(),
    agencyId: uuid("agency_id").references(() => agency.id, {
        onDelete: "set null",
    }),
    id: uuid("id")
        .default(sql `uuid_generate_v4()`)
        .primaryKey()
        .notNull(),
    firebaseId: varchar("firebase_id", { length: 128 }),
    verifiedAt: timestamp("verified_at", {
        withTimezone: true,
        mode: "string",
    }),
    creationDate: timestamp("creation_date", {
        withTimezone: true,
        mode: "string",
    })
        .defaultNow()
        .notNull(),
}, (table) => [unique("app_user_firebase_id_key").on(table.firebaseId)]);
export const appUserRelations = relations(appUser, ({ one, many }) => ({
    account: one(account, {
        fields: [appUser.accountId],
        references: [account.id],
    }),
    agency: one(agency, {
        fields: [appUser.agencyId],
        references: [agency.id],
    }),
    intermediaryProfile: one(intermediaryProfile, {
        fields: [appUser.id],
        references: [intermediaryProfile.appUserId],
    }),
    dossierUser: many(dossierUser),
    notifications: many(notification),
    conversation: many(conversationTable),
    documentTokens: many(dossierDocumentToken),
    createdConnections: many(agencyServiceProviderConnection),
    ownedDossiers: many(dossier),
    ownedWorkItems: many(workItem, { relationName: "workItemOwner" }),
    resolvedWorkItems: many(workItem, {
        relationName: "workItemResolvedBy",
    }),
}));
export const intermediaryProfile = pgTable("intermediary_profile", {
    appUserId: uuid("app_user_id")
        .references(() => appUser.id, {
        onDelete: "cascade",
        onUpdate: "cascade",
    })
        .primaryKey()
        .notNull(),
    street: varchar("street", { length: 255 }),
    housenumber: varchar("housenumber", { length: 20 }),
    housenumberAddition: varchar("housenumber_addition", { length: 20 }),
    postalcode: varchar("postalcode", { length: 10 }),
    placename: varchar("placename", { length: 255 }),
    lastName: varchar("last_name", { length: 80 }),
    initials: varchar("initials", { length: 20 }),
    creationDate: timestamp("creation_date", {
        withTimezone: true,
        mode: "string",
    })
        .defaultNow()
        .notNull(),
});
export const intermediaryProfileRelations = relations(intermediaryProfile, ({ one }) => ({
    appUser: one(appUser, {
        fields: [intermediaryProfile.appUserId],
        references: [appUser.id],
    }),
}));
export const version = pgTable("version", {
    name: varchar("name", { length: 255 }),
    dossierId: uuid("dossier_id")
        .references(() => dossier.id, {
        onDelete: "cascade",
    })
        .notNull(),
    lastEdited: timestamp("last_edited", {
        withTimezone: true,
        mode: "string",
    }),
    versionData: jsonb("version_data").$type(),
    id: uuid("id")
        .default(sql `uuid_generate_v4()`)
        .primaryKey()
        .notNull(),
    creationDate: timestamp("creation_date", {
        withTimezone: true,
        mode: "string",
    })
        .defaultNow()
        .notNull(),
});
export const versionRelations = relations(version, ({ one, many }) => ({
    dossier: one(dossier, {
        fields: [version.dossierId],
        references: [dossier.id],
    }),
    dossierVersionNotes: one(dossierVersionNotes, {
        fields: [version.id],
        references: [dossierVersionNotes.versionId],
    }),
    mutations: many(versionMutation),
}));
export const createdDossiersPerUserPerDay = pgView("created_dossiers_per_user_per_day", {
    date: date("date"),
    agencyId: uuid("agency_id"),
    userId: uuid("user_id"),
    userFullName: varchar("user_full_name", { length: 80 }),
    agencyName: varchar("agency_name", { length: 255 }),
    count: bigint("count", { mode: "number" }),
});
export const versionMutation = pgTable("version_mutation", {
    versionId: uuid("version_id")
        .references(() => version.id, {
        onDelete: "cascade",
    })
        .notNull(),
    versionData: jsonb("version_data").$type(),
    versionMutation: jsonb("version_mutation").$type(),
    by: uuid("by").references(() => appUser.id, { onDelete: "set null" }),
    id: uuid("id")
        .default(sql `uuid_generate_v4()`)
        .primaryKey()
        .notNull(),
    mutationDate: timestamp("mutation_date", {
        withTimezone: true,
        mode: "string",
    })
        .defaultNow()
        .notNull(),
    createdAt: timestamp("created_at", {
        withTimezone: true,
        mode: "string",
    })
        .defaultNow()
        .notNull(),
});
export const versionMutationRelations = relations(versionMutation, ({ one }) => ({
    version: one(version, {
        fields: [versionMutation.versionId],
        references: [version.id],
    }),
}));
export const dossierVersionNotes = pgTable("dossier_version_notes", {
    versionId: uuid("version_id")
        .primaryKey()
        .notNull()
        .references(() => version.id, {
        onDelete: "cascade",
    }),
    lastChanged: timestamp("last_changed", {
        withTimezone: true,
        mode: "string",
    })
        .defaultNow()
        .notNull(),
    notes: text("notes"),
    changedBy: uuid("changed_by").references(() => appUser.id, {
        onDelete: "set null",
    }),
    createdAt: timestamp("created_at", {
        withTimezone: true,
        mode: "string",
    })
        .defaultNow()
        .notNull(),
});
export const dossierVersionNotesRelations = relations(dossierVersionNotes, ({ one }) => ({
    version: one(version, {
        fields: [dossierVersionNotes.versionId],
        references: [version.id],
    }),
    editor: one(appUser, {
        fields: [dossierVersionNotes.changedBy],
        references: [appUser.id],
    }),
}));
export const dossierDocumentToken = pgTable("dossier_document_token", {
    documentId: uuid("document_id").notNull(),
    ownerId: uuid("owner_id")
        .references(() => appUser.id, {
        onDelete: "cascade",
    })
        .notNull(),
    uploadUrl: text("upload_url").notNull(),
    creationDate: timestamp("creation_date", {
        withTimezone: true,
        mode: "string",
    })
        .defaultNow()
        .notNull(),
    expiresAt: timestamp("expires_at", {
        withTimezone: true,
        mode: "string",
    }),
    id: uuid("id")
        .default(sql `uuid_generate_v4()`)
        .primaryKey()
        .notNull(),
    agencyId: uuid("agency_id")
        .references(() => agency.id, {
        onDelete: "cascade",
    })
        .notNull(),
    dossierId: uuid("dossier_id").references(() => dossier.id, {
        onDelete: "cascade",
    }),
});
export const dossierDocumentTokenRelations = relations(dossierDocumentToken, ({ one }) => ({
    dossier: one(dossier, {
        fields: [dossierDocumentToken.dossierId],
        references: [dossier.id],
    }),
    agency: one(agency, {
        fields: [dossierDocumentToken.agencyId],
        references: [agency.id],
    }),
    dossierDocument: one(dossierDocument, {
        fields: [dossierDocumentToken.documentId],
        references: [dossierDocument.id],
    }),
    owner: one(appUser, {
        fields: [dossierDocumentToken.ownerId],
        references: [appUser.id],
    }),
}));
export const fundaProperties = pgTable("funda_properties", {
    fundaId: text("funda_id").notNull(),
    fundaUrl: text("funda_url").notNull(),
    fundaListingData: jsonb("funda_listing_data"),
    fundaImageUrls: jsonb("funda_image_urls").$type(),
    fundaImageIds: jsonb("funda_image_ids").$type(),
    fundaPriceNum: numeric("funda_price_num", { precision: 12, scale: 2 }),
    fundaPriceStr: text("funda_price_str"),
    fundaIsAuction: boolean("funda_is_auction").default(false).notNull(),
    fundaDescription: text("funda_description"),
    fundaAddressLine5: text("funda_address_line_5"),
    fundaAddressLine6: text("funda_address_line_6"),
    fundaDetails: jsonb("funda_details"),
    kadasterPostcode: text("kadaster_postcode"),
    kadasterWoonplaats: text("kadaster_woonplaats"),
    kadasterStraat: text("kadaster_straat"),
    kadasterHuisnummer: text("kadaster_huisnummer"),
    kadasterHuisletter: text("kadaster_huisletter"),
    kadasterHuisnummertoevoeging: text("kadaster_huisnummertoevoeging"),
    kadasterAdresseerbaarObjectIdentificatie: text("kadaster_adresseerbaar_object_identificatie"),
    id: uuid("id")
        .default(sql `uuid_generate_v4()`)
        .primaryKey()
        .notNull(),
    creationDate: timestamp("creation_date", {
        withTimezone: true,
        mode: "string",
    })
        .defaultNow()
        .notNull(),
});
export const agencyServiceProviderConnection = pgTable("agency_service_provider_connection", {
    agencyId: uuid("agency_id").notNull(),
    serviceProviderId: uuid("service_provider_id").notNull(),
    connectionNumber: text("connection_number"),
    connectedBy: uuid("connected_by").references(() => appUser.id, {
        onDelete: "set null",
    }),
    activeSince: timestamp("active_since", {
        withTimezone: true,
        mode: "string",
    }).defaultNow(),
    createdAt: timestamp("created_at", {
        withTimezone: true,
        mode: "string",
    })
        .defaultNow()
        .notNull(),
}, (table) => [
    primaryKey({
        name: "agency_service_provider_connection_pkey",
        columns: [table.agencyId, table.serviceProviderId],
    }),
]);
export const agencyServiceProviderConnectionRelations = relations(agencyServiceProviderConnection, ({ one }) => ({
    agency: one(agency, {
        fields: [agencyServiceProviderConnection.agencyId],
        references: [agency.id],
    }),
    connector: one(appUser, {
        fields: [agencyServiceProviderConnection.connectedBy],
        references: [appUser.id],
    }),
    serviceProvider: one(hdnServiceProvider, {
        fields: [agencyServiceProviderConnection.serviceProviderId],
        references: [hdnServiceProvider.id],
    }),
}));
